import * as THREE from 'three';
import type { CollisionShape } from '../systems/Collision.js';

/**
 * Base class for all game entities.
 *
 * Entities have:
 * - 2D position (x, y) - maps to Three.js (x, z) plane
 * - Rotation (facing angle in radians)
 * - Optional visual representation (Three.js mesh)
 * - Update lifecycle hook
 * - Cleanup method
 * - Optional collision shape (for collision detection)
 *
 * Extend this class to create specific entity types (Mech, Projectile, etc.)
 */
export abstract class Entity {
  /**
   * 2D position in game world.
   * x: horizontal position
   * y: vertical position (maps to Z axis in Three.js)
   */
  position: { x: number; y: number };

  /**
   * Facing angle in radians.
   * 0 = facing right (+X), Math.PI/2 = facing up (+Y/+Z)
   */
  rotation: number;

  /**
   * Visual representation in the Three.js scene.
   * Can be null for entities without visual representation.
   */
  mesh: THREE.Object3D | null;

  /**
   * Internal flag to track if the entity has been destroyed.
   */
  private _destroyed: boolean = false;

  constructor(x: number = 0, y: number = 0, rotation: number = 0) {
    this.position = { x, y };
    this.rotation = rotation;
    this.mesh = null;
  }

  /**
   * Update entity state for the current fixed timestep.
   * Override this to implement entity-specific behavior.
   *
   * @param dt - Fixed delta time in milliseconds (typically 16.67ms)
   */
  abstract update(dt: number): void;

  /**
   * Get the collision shape for this entity.
   * Override this to provide collision detection for the entity.
   *
   * @returns Collision shape, or null if entity has no collision
   */
  getCollisionShape(): CollisionShape | null {
    return null; // Default: no collision
  }

  /**
   * Synchronize Three.js mesh position/rotation with entity's logical state.
   * Call this after updating position/rotation if the entity has a mesh.
   */
  protected updateMeshTransform(): void {
    if (this.mesh) {
      // Map 2D game coordinates to Three.js 3D space
      // Game (x, y) -> Three.js (x, 0, z)
      this.mesh.position.set(this.position.x, 0, this.position.y);

      // Rotation around Y axis (vertical in Three.js)
      this.mesh.rotation.y = this.rotation;
    }
  }

  /**
   * Check if the entity has been destroyed.
   */
  isDestroyed(): boolean {
    return this._destroyed;
  }

  /**
   * Clean up resources when the entity is removed from the game.
   * Override this to add custom cleanup logic, but always call super.destroy().
   */
  destroy(): void {
    this._destroyed = true;

    // Dispose of geometry and materials if present
    if (this.mesh) {
      this.mesh.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          if (child.geometry) {
            child.geometry.dispose();
          }
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((mat) => mat.dispose());
            } else {
              child.material.dispose();
            }
          }
        }
      });
    }
  }
}
